<template>
  <div>
    <template v-if="readOnly">
      <div v-if="columnDataValue && !columnDataValue.isValid" class="min-h-[1.2rem] p-2 text-danger">
        {{ columnDataValue.errorMessage }}
      </div>
      <div v-else-if="columnDataValue && columnDataValue.text && columnDataValue.isValid"
           class="min-h-[1.2rem] p-2"
           :class="{ 'overflow-hidden text-ellipsis whitespace-nowrap': singleLine }">
        {{ columnDataValue.text }}
      </div>
      <div v-else-if="displayValue"
           class="min-h-[1.2rem] p-2"
           :class="{ 'overflow-hidden text-ellipsis whitespace-nowrap': singleLine }">
        {{ displayValue }}
      </div>
      <div v-else class="min-h-[1.2rem] p-2 italic text-fg-muted">
        (blank)
      </div>
    </template>
    <template v-else>
      <BaseAutoComplete
        :id="id"
        :model-value="inputValue"
        :placeholder="placeholderText"
        :options="autocompleteOptionItems"
        :loading="autocompleteLoading"
        :has-more="autocompleteHasMore"
        :prefer-model-value-display="header.relationLookupEnabled === true"
        :is-clearable="header.isNullable"
        :error="fieldError"
        @update:model-value="changed"
        @request-options="onRequestOptions"
      />
      <BaseProgressBar v-if="saving" class="h-1 m-0 p-0" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import BaseProgressBar from '@/components/BaseComponents/BaseProgressBar.vue'
import BaseAutoComplete from '@/components/BaseComponents/BaseAutoComplete.vue'
import { useTableStore } from '@/store/storeTable'
import { useCellSave } from '@/composables/useCellSave'
import { fetchDataViewRelationLookup } from '@/api/endpoints/dataViews'
import { useAutoCompleteOptions } from '@/composables/useAutoCompleteOptions'

// The plain value an autocomplete cell carries once the surrounding column metadata is stripped.
type ScalarValue = string | number | null

interface ColumnData {
  isValid: boolean
  errorMessage?: string
  text?: string
  value?: ScalarValue
}

interface Props {
  modelValue?: ColumnData | ScalarValue
  readOnly?: boolean
  singleLine?: boolean
  autoSave?: boolean
  rowId?: string | number
  header: {
    relationLookupEnabled?: boolean
    dataViewId?: string
    dataViewColumnId?: string
    isNullable?: boolean
    field?: string
    [key: string]: any
  }
  onSaveSuccess?: () => void | Promise<void>
  /**
   * US 3399: valgfri id som settes paa selve kontrollen, saa en etikett kan knyttes med for=.
   * Uten den erklaeringen ville Vues attribute-fallthrough lagt id-en paa rot-diven, og en
   * for=-peker dit navngir ingenting for en skjermleser selv om statisk analyse blir fornoeyd.
   */
  id?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  readOnly: false,
  singleLine: false,
  autoSave: true,
  rowId: ''
})

const tableStore = useTableStore()
const emit = defineEmits<{
  'update:modelValue': [value: ColumnData | ScalarValue]
}>()

const { saving, saveErrorMessage, saveCell } = useCellSave()

function fetchRelationLookupOptions (term: string, signal: AbortSignal) {
  if (props.header.relationLookupEnabled !== true) {
    return Promise.resolve({ options: [], hasMore: false })
  }
  if (!props.header.dataViewId || !props.header.dataViewColumnId) {
    return Promise.resolve({ options: [], hasMore: false })
  }
  return fetchDataViewRelationLookup(props.header.dataViewId, props.header.dataViewColumnId, term, 20, signal).then((result) => ({
    options: result.items.map((item) => ({ value: item.value, label: item.label })),
    hasMore: result.hasMore
  }))
}

const {
  options: autocompleteOptionItems,
  loading: autocompleteLoading,
  error: autocompleteError,
  hasMore: autocompleteHasMore,
  fetch: fetchAutocompleteOptions
} = useAutoCompleteOptions(fetchRelationLookupOptions, { debounceMs: 300 })

function onRequestOptions (payload: { term: string }) {
  fetchAutocompleteOptions(payload.term)
}

function isColumnData (value: ColumnData | ScalarValue): value is ColumnData {
  return value !== null && typeof value === 'object' && 'isValid' in value
}

const columnDataValue = computed((): ColumnData | null => {
  return isColumnData(props.modelValue) ? props.modelValue : null
})

const displayValue = computed((): string | null => {
  const colData = columnDataValue.value
  if (colData) {
    return colData.text || null
  }
  if (props.modelValue === null || props.modelValue === undefined) {
    return null
  }
  return String(props.modelValue)
})

const inputValue = computed((): ScalarValue => {
  const colData = columnDataValue.value
  if (colData) {
    return colData.value ?? null
  }
  return props.modelValue as ScalarValue
})

const placeholderText = computed((): string => {
  const colData = columnDataValue.value
  if (colData && colData.text) {
    return colData.text
  }
  return '(Søk...)'
})

const fieldError = computed(() => {
  if (saveErrorMessage.value) return saveErrorMessage.value
  const err = autocompleteError.value
  return err ? String(err.message || '').trim() : ''
})

function toScalarModelValue (mv: ColumnData | ScalarValue | undefined): ScalarValue {
  if (mv === null || mv === undefined) return null
  if (isColumnData(mv)) return mv.value ?? null
  return mv as string | number
}

type AutocompleteOption = (typeof autocompleteOptionItems.value)[number]

function resolveOption (newValue: ScalarValue): AutocompleteOption | undefined {
  return newValue == null
    ? undefined
    : autocompleteOptionItems.value.find((o) => String(o.value) === String(newValue))
}

function emitRefreshedText (newValue: ScalarValue, option: AutocompleteOption | undefined): void {
  if (newValue != null && option != null) {
    const currentText = columnDataValue.value?.text
    if (currentText !== option.label) {
      const base = columnDataValue.value
      emit(
        'update:modelValue',
        base
          ? { ...base, value: newValue ?? undefined, text: option.label, isValid: true }
          : { value: newValue, text: option.label, isValid: true }
      )
    }
  }
}

function buildChangePayload (newValue: ScalarValue, option: AutocompleteOption | undefined): ColumnData | ScalarValue {
  if (newValue === null) {
    return null
  } else if (option != null) {
    return {
      value: newValue,
      text: props.header.relationLookupEnabled === true ? String(newValue) : option.label,
      isValid: true
    }
  } else if (columnDataValue.value) {
    return {
      ...columnDataValue.value,
      value: newValue ?? undefined,
      text: String(newValue),
      isValid: true,
      errorMessage: undefined
    }
  } else {
    return newValue
  }
}

async function changed (newValue: ScalarValue): Promise<void> {
  if (newValue === "IcsNullValue_") {
    newValue = null;
  }

  const prevScalar = toScalarModelValue(props.modelValue)
  const option = resolveOption(newValue)

  if (prevScalar === newValue) {
    emitRefreshedText(newValue, option)
    return
  }

  const payload = buildChangePayload(newValue, option)

  emit('update:modelValue', payload)

  if (!props.autoSave) return

  const item = {
    tableKey: String(props.header?.tableKey || tableStore.tableKey || '').trim(),
    rowId: props.rowId,
    columnName: props.header.field,
    oldValue: prevScalar,
    newValue: newValue
  }
  await saveCell(item, props.onSaveSuccess ?? null)
}
</script>

